/**
 * WalletPage — `/wallet`  (superall-shell-02)
 *
 * Sections
 * ─────────
 * 1. Balance card    — current balance with SAR suffix, error/loading states
 * 2. Top-up CTA      — renders a bottom-sheet modal (coming-soon copy)
 * 3. Transaction list — paginated ledger via useWalletTransactions; load-more
 *
 * Accessibility: all interactive controls are reachable by keyboard; amounts
 * are announced with a visually-hidden currency label; the loading skeleton
 * has role="status" + aria-live="polite".
 *
 * RTL: flex direction and text alignment are set via logical properties
 * (start/end) so Arabic layouts flip automatically.
 */

import * as React from 'react';
import { MotionCard, AnimatedNumber, StaggerList, StaggerItem, FadeIn } from '@workspace/ui';

import { useWalletBalance, useWalletTransactions, type WalletTxType } from '../hooks/useWallet';
import { useAuth } from '../providers/AuthProvider';
import { useLang, useT } from '../providers/LangProvider';

// ── Moyasar ───────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    Moyasar?: { init: (config: Record<string, unknown>) => void };
  }
}

const MOYASAR_KEY = (import.meta.env.VITE_MOYASAR_PUBLISHABLE_KEY as string) ?? '';

function useMoyasarScript() {
  React.useEffect(() => {
    if (document.getElementById('moyasar-script')) return;
    const script = document.createElement('script');
    script.id = 'moyasar-script';
    script.src = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.js';
    script.async = true;
    document.head.appendChild(script);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.moyasar.com/mpf/1.14.0/moyasar.css';
    document.head.appendChild(link);
  }, []);
}

// ── helpers ──────────────────────────────────────────────────────────────────

function formatAmount(amount: number, currency: string, type: WalletTxType): string {
  const abs = Math.abs(amount).toLocaleString('en-SA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign =
    type === 'credit' || type === 'earn' || type === 'refund' || type === 'topup' ? '+' : '−';
  return `${sign}${abs} ${currency}`;
}

function amountColor(type: WalletTxType): string {
  if (type === 'credit' || type === 'earn' || type === 'refund' || type === 'topup') {
    return 'text-emerald-700';
  }
  return 'text-neutral-900 dark:text-neutral-100';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── sub-components ────────────────────────────────────────────────────────────

function BalanceSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading wallet balance"
      className="mb-6 animate-pulse rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 shadow-sm px-5 py-6"
    >
      <div className="h-3 w-28 rounded bg-neutral-200 dark:bg-neutral-700 mb-3" />
      <div className="h-10 w-48 rounded bg-neutral-200 dark:bg-neutral-700" />
    </div>
  );
}

function TopUpModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [lang] = useLang();
  const isAr = lang === 'ar';
  const { token } = useAuth();
  const [amount, setAmount] = React.useState(100);
  const [phase, setPhase] = React.useState<'amount' | 'payment'>('amount');
  const [error, setError] = React.useState<string | null>(null);
  const mountedRef = React.useRef(false);

  useMoyasarScript();

  function mountMoyasar() {
    if (mountedRef.current || !window.Moyasar) return;
    mountedRef.current = true;
    window.Moyasar.init({
      element: '.moyasar-topup-form',
      amount: Math.round(amount * 100),
      currency: 'SAR',
      description: isAr ? 'شحن محفظة Superall' : 'Superall wallet top-up',
      publishable_api_key: MOYASAR_KEY,
      callback_url: window.location.origin + '/wallet',
      methods: ['creditcard', 'mada', 'applepay', 'stcpay'],
      on_completed: async (payment: { id: string; status: string }) => {
        if (payment.status !== 'paid') {
          setError(isAr ? 'فشل الدفع' : 'Payment failed');
          return;
        }
        try {
          await fetch('/api/platform/v1/wallet/topup', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ paymentId: payment.id, amountSar: amount }),
          });
        } catch {
          /* balance will sync on next poll */
        }
        onClose();
      },
    });
  }

  React.useEffect(() => {
    if (phase === 'payment') {
      const timer = setTimeout(mountMoyasar, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [phase]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="topup-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl bg-white dark:bg-neutral-900 px-6 py-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="mb-3 text-2xl" aria-hidden="true">
          💳
        </div>
        <h2
          id="topup-title"
          className="mb-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100"
        >
          {isAr ? 'شحن المحفظة' : 'Top up wallet'}
        </h2>

        {phase === 'amount' && (
          <>
            {!MOYASAR_KEY && (
              <p className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                {isAr
                  ? 'بوابة الدفع قيد الإعداد — تواصل مع الفريق.'
                  : 'Payment gateway pending configuration.'}
              </p>
            )}
            <label className="block mb-1 text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {isAr ? 'المبلغ (ريال)' : 'Amount (SAR)'}
            </label>
            <input
              type="number"
              min={10}
              max={5000}
              step={1}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-4 py-2.5 text-lg font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-xl border border-neutral-200 dark:border-neutral-700 px-4 py-2.5 text-sm font-semibold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                {t.walletPage.close}
              </button>
              <button
                type="button"
                disabled={!MOYASAR_KEY || amount < 10 || amount > 5000}
                onClick={() => setPhase('payment')}
                className="flex-1 rounded-xl bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-50"
              >
                {isAr ? 'متابعة' : 'Continue'}
              </button>
            </div>
          </>
        )}

        {phase === 'payment' && (
          <>
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <div className="moyasar-topup-form" />
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full rounded-xl border border-neutral-200 dark:border-neutral-700 px-4 py-2 text-sm text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              {t.walletPage.close}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function WalletPage(): React.ReactElement {
  const t = useT();
  const { data: balance, loading: balLoading, error: balError } = useWalletBalance();
  const {
    items,
    loading: txLoading,
    loadingMore,
    error: txError,
    hasMore,
    loadMore,
  } = useWalletTransactions();
  const [showTopUp, setShowTopUp] = React.useState(false);

  return (
    <FadeIn>
      <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
        {t.walletPage.title}
      </h1>

      {/* ── Balance card ─────────────────────────────── */}
      {balLoading ? (
        <BalanceSkeleton />
      ) : balError ? (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/30 px-5 py-5 shadow-sm">
          <p className="text-sm font-medium text-red-700 dark:text-red-400">
            {t.walletPage.errorTitle}
          </p>
        </div>
      ) : (
        <MotionCard hoverScale={1.01} tapScale={0.99} className="mb-6 rounded-2xl">
          <section
            aria-label={t.walletPage.balance}
            className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-gradient-to-br from-purple-700 to-purple-900 px-5 py-6 shadow-sm"
          >
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-purple-200">
              {t.walletPage.balance}
            </p>
            <p className="text-4xl font-bold text-white tabular-nums">
              {balance ? (
                <AnimatedNumber
                  value={balance.balance}
                  format={(n) =>
                    n.toLocaleString('en-SA', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }
                />
              ) : (
                '—'
              )}
              <span className="ms-2 text-xl font-medium text-purple-200">
                {balance?.currency ?? t.walletPage.currencySuffix}
              </span>
            </p>

            <button
              type="button"
              onClick={() => setShowTopUp(true)}
              className="mt-4 rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-purple-800"
            >
              {t.walletPage.topUp}
            </button>
          </section>
        </MotionCard>
      )}

      {/* ── Transaction ledger ───────────────────────── */}
      <section aria-label={t.walletPage.transactions}>
        <h2 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300">
          {t.walletPage.transactions}
        </h2>

        {txLoading ? (
          <div
            role="status"
            aria-live="polite"
            aria-label={t.walletPage.loading}
            className="space-y-2"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700"
              />
            ))}
          </div>
        ) : txError ? (
          <p className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/30 px-4 py-4 text-sm text-red-700 dark:text-red-400">
            {t.walletPage.transactionLoadError}
          </p>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-8 text-center">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {t.walletPage.noTransactions}
            </p>
          </div>
        ) : (
          <>
            <StaggerList className="space-y-1.5" as="ul">
              {items.map((tx) => {
                const typeKey = `type_${tx.type}` as keyof typeof t.walletPage;
                const label =
                  typeof t.walletPage[typeKey] === 'string'
                    ? (t.walletPage[typeKey] as string)
                    : tx.type;
                return (
                  <StaggerItem key={tx.id} as="li">
                    <MotionCard hoverScale={1.01} tapScale={0.99} className="rounded-xl">
                      <div className="flex items-center justify-between rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 text-sm">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-neutral-900 dark:text-neutral-100">
                            {tx.description ?? label}
                          </p>
                          <p className="text-xs text-neutral-400 dark:text-neutral-500">
                            {label} · {formatDate(tx.createdAt)}
                          </p>
                        </div>
                        <span
                          className={`ms-4 shrink-0 font-semibold tabular-nums ${amountColor(tx.type)}`}
                        >
                          {formatAmount(tx.amount, tx.currency, tx.type)}
                        </span>
                      </div>
                    </MotionCard>
                  </StaggerItem>
                );
              })}
            </StaggerList>

            {hasMore && (
              <button
                type="button"
                disabled={loadingMore}
                onClick={loadMore}
                className="mt-4 w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                {loadingMore ? t.walletPage.loading : t.walletPage.loadMore}
              </button>
            )}
          </>
        )}
      </section>

      {showTopUp && <TopUpModal onClose={() => setShowTopUp(false)} />}
    </FadeIn>
  );
}
